import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Crown, Play, ChevronRight } from 'lucide-react';
import { MilestoneCelebration } from '../components/MilestoneCelebration';
import LegalFooter from '../components/LegalFooter';
import { getEmbedUrl } from '../components/course/LessonPlayer';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CLASS_IMG = 'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600';

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const Eyebrow = ({ children }) => (
  <p className="text-[#D4A24A] text-xs font-semibold uppercase tracking-[0.2em] mb-4">{children}</p>
);

export default function Landing() {
  const [potData, setPotData] = useState({ current: 0, goal: 1000000 });
  const [topPerformers, setTopPerformers] = useState([]);
  const [founderStatus, setFounderStatus] = useState({ seats_remaining: 50 });
  const [tiers, setTiers] = useState({});
  const [landingVideo, setLandingVideo] = useState({ exists: false });

  useEffect(() => {
    (async () => {
      try {
        const [potRes, topRes, founderRes, tiersRes, videoRes] = await Promise.all([
          axios.get(`${API_URL}/api/pot/status`),
          axios.get(`${API_URL}/api/leaderboard/top`).catch(() => ({ data: [] })),
          axios.get(`${API_URL}/api/memberships/founder-status`).catch(() => ({ data: { seats_remaining: 50 } })),
          axios.get(`${API_URL}/api/memberships/tiers`).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/videos/landing/exists`).catch(() => ({ data: { exists: false } })),
        ]);
        setPotData({ current: potRes.data.total_amount || 0, goal: potRes.data.goal_amount || 1000000 });
        setTopPerformers(topRes.data);
        setFounderStatus(founderRes.data);
        setTiers(tiersRes.data);
        setLandingVideo(videoRes.data);
      } catch (e) {
        console.error('Failed to fetch landing data:', e);
      }
    })();
  }, []);

  const potPct = Math.min(100, (potData.current / (potData.goal || 1)) * 100);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <MilestoneCelebration />

      {/* ===== Navigation ===== */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0A0A0B]/80 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img src="/wemillionaires-logo.png" alt="We Masterminds" className="h-9 w-auto" />
            <span className="text-lg font-bold tracking-wide hidden sm:block">WE MASTERMINDS</span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <a href="#momentum" className="text-white/60 hover:text-[#D4A24A] transition-colors text-xs uppercase tracking-[0.2em]">Momentum</a>
            <a href="#masterclass" className="text-white/60 hover:text-[#D4A24A] transition-colors text-xs uppercase tracking-[0.2em]">Training</a>
            <a href="#membership" className="text-white/60 hover:text-[#D4A24A] transition-colors text-xs uppercase tracking-[0.2em]">Membership</a>
            <Link to="/directory" className="text-white/60 hover:text-[#D4A24A] transition-colors text-xs uppercase tracking-[0.2em]">Directory</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-white/70 hover:text-white text-sm transition-colors px-3 py-2" data-testid="login-btn">Sign in</Link>
            <Link
              to="/register"
              className="border border-[#D4A24A]/60 text-[#D4A24A] hover:bg-[#D4A24A] hover:text-black text-sm font-semibold px-5 py-2 rounded-sm transition-colors uppercase tracking-wider"
              data-testid="register-btn"
            >
              Join
            </Link>
          </div>
        </div>
      </nav>

      {/* ===== Hero ===== */}
      <section className="relative min-h-[92vh] flex items-end md:items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0A0A0B]">
          <div
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage: 'url(/wemillionaires-logo.png)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
            data-testid="hero-logo-bg"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(10,10,11,0.15) 0%, rgba(10,10,11,0.5) 55%, #0A0A0B 100%)' }} />
        </div>
        <motion.div variants={stagger} initial="hidden" animate="show" className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24 md:py-40 text-center w-full">
          <motion.p variants={fadeUp} className="text-[#D4A24A] text-xs md:text-sm font-semibold uppercase tracking-[0.2em] mb-6">
            Performance. Recognition. Reward.
          </motion.p>
          <motion.h1 variants={fadeUp} className="font-playfair text-5xl md:text-7xl font-medium tracking-tight mb-6">
            Build wealth with precision.
          </motion.h1>
          <motion.p variants={fadeUp} className="text-white/70 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed font-light">
            A performance-based membership platform where success is measured, recognized,
            and rewarded through real sales performance—not promises.
          </motion.p>
          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/register"
              className="h-13 px-8 py-4 bg-[#D4A24A] text-black font-semibold rounded-sm hover:bg-[#F59E0B] transition-colors flex items-center gap-2 text-sm uppercase tracking-wider"
              style={{ boxShadow: '0 0 25px rgba(212,162,74,0.25)' }}
              data-testid="cta-founder"
            >
              Request founder access <ArrowRight size={16} />
            </Link>
            <a
              href="#membership"
              className="h-13 px-8 py-4 border border-white/20 text-white/90 hover:border-[#D4A24A] hover:text-[#D4A24A] rounded-sm transition-colors text-sm uppercase tracking-wider"
              data-testid="cta-explore"
            >
              Explore membership
            </a>
          </motion.div>
          <motion.p variants={fadeUp} className="mt-8 text-white/40 text-xs uppercase tracking-[0.2em]" data-testid="founder-seats-alert">
            Founder seats remaining: <span className="text-[#D4A24A]">{founderStatus?.seats_remaining ?? 50}</span>
          </motion.p>
        </motion.div>
      </section>

      {/* ===== Measured Momentum ===== */}
      <section id="momentum" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="max-w-3xl mb-16">
            <Eyebrow>Measured momentum</Eyebrow>
            <h2 className="font-playfair text-3xl md:text-5xl font-medium tracking-tight mb-4">
              A community built around visible performance.
            </h2>
            <p className="text-white/60 text-lg font-light leading-relaxed">
              See the standards, the progress, and the learning environment. WeMasterminds gives
              serious entrepreneurs a clearer path to build, improve, and be recognized.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Leaderboard */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
              className="bg-[#141416] border border-white/[0.08] rounded-lg p-8 hover:border-[#D4A24A]/30 transition-colors"
              data-testid="landing-leaderboard"
            >
              <div className="flex items-center gap-3 mb-8">
                <Crown className="w-5 h-5 text-[#D4A24A]" />
                <h3 className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold">Founder leaderboard</h3>
              </div>
              <div className="space-y-1">
                {topPerformers.length > 0 ? topPerformers.slice(0, 5).map((founder, idx) => (
                  <div key={founder.id || idx} className="flex items-center gap-4 py-3 border-b border-white/5 last:border-0">
                    <span className={`font-playfair text-2xl w-8 ${idx === 0 ? 'text-[#D4A24A]' : 'text-white/30'}`}>{idx + 1}</span>
                    <p className="flex-1 font-medium text-white/90">{founder.name}</p>
                    <span className="font-mono text-sm text-white/60">${founder.total_earnings?.toLocaleString() || '0'}</span>
                  </div>
                )) : (
                  <p className="text-white/40 text-sm py-8 text-center">Be the first to join!</p>
                )}
              </div>
            </motion.div>

            {/* Thermometer */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
              className="bg-[#141416] border border-white/[0.08] rounded-lg p-8 flex flex-col justify-center hover:border-[#D4A24A]/30 transition-colors"
              data-testid="landing-thermometer"
            >
              <h3 className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-8">Millionaire thermometer</h3>
              <div className="flex items-baseline justify-between mb-4">
                <span className="font-playfair text-4xl md:text-5xl text-[#D4A24A]">${potData.current.toLocaleString()}</span>
                <span className="text-white/40 text-sm">Goal: ${potData.goal.toLocaleString()}</span>
              </div>
              <div className="h-3 rounded-full bg-white/5 overflow-hidden mb-6">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: `${Math.max(potPct, 1)}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.4, ease: 'easeOut' }}
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #D4A24A, #F59E0B)', boxShadow: '0 0 25px rgba(212,162,74,0.4)' }}
                />
              </div>
              <p className="text-white/50 text-sm font-light">Every verified sale moves the community toward a shared milestone.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Featured Masterclass ===== */}
      <section id="masterclass" className="py-24 md:py-32 bg-[#141416]/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }}
              className="lg:col-span-7 rounded-lg overflow-hidden border border-white/[0.08]"
              style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
            >
              {!landingVideo.exists ? (
                <div className="relative aspect-video">
                  <img src={CLASS_IMG} alt="Entrepreneur leading a focused business masterclass" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-16 h-16 bg-[#D4A24A] rounded-full flex items-center justify-center" style={{ boxShadow: '0 0 40px rgba(212,162,74,0.4)' }}>
                      <Play className="w-7 h-7 text-black ml-1" />
                    </div>
                  </div>
                </div>
              ) : ['youtube', 'vimeo', 'iframe'].includes(landingVideo.source_type) ? (
                <iframe
                  src={getEmbedUrl(landingVideo.external_url)}
                  title={landingVideo.title || 'Featured Masterclass'}
                  className="w-full aspect-video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  data-testid="landing-video-iframe"
                />
              ) : (
                <video
                  src={landingVideo.source_type === 'direct' && landingVideo.external_url
                    ? landingVideo.external_url
                    : `${API_URL}/api/videos/landing/stream`}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full aspect-video object-cover"
                  data-testid="landing-video-player"
                />
              )}
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="lg:col-span-5">
              <Eyebrow>Featured masterclass</Eyebrow>
              <h3 className="font-playfair text-3xl md:text-4xl font-medium tracking-tight mb-4">
                {landingVideo.title || 'Sales strategy, built to apply.'}
              </h3>
              <p className="text-white/60 text-lg font-light leading-relaxed mb-8">
                {landingVideo.description || 'Practical instruction designed for your next conversation, campaign, and close.'}
              </p>
              <Link to="/register" className="inline-flex items-center gap-2 text-[#D4A24A] text-sm font-semibold uppercase tracking-[0.2em] hover:gap-3 transition-[gap]" data-testid="masterclass-cta">
                Start learning <ChevronRight size={16} />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== Pillars ===== */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="max-w-3xl mb-16">
            <Eyebrow>The WeMasterminds standard</Eyebrow>
            <h2 className="font-playfair text-3xl md:text-5xl font-medium tracking-tight mb-4">
              Serious tools for serious entrepreneurs.
            </h2>
            <p className="text-white/60 text-lg font-light leading-relaxed">
              Membership brings together sales opportunity, education, digital business tools,
              and community—so progress has a system behind it.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: '01', title: 'A path you can measure', body: 'Track real performance instead of relying on vague motivation.' },
              { n: '02', title: 'Training you can use', body: 'Build stronger sales fundamentals and advanced strategy.' },
              { n: '03', title: 'A network built to move', body: 'Connect with entrepreneurs who take execution seriously.' },
            ].map((p) => (
              <motion.div
                key={p.n}
                variants={fadeUp}
                className="bg-[#141416] border border-white/[0.08] rounded-lg p-8 hover:border-[#D4A24A]/30 hover:-translate-y-0.5 transition-[border-color,transform]"
                data-testid={`pillar-${p.n}`}
              >
                <span className="font-playfair text-5xl text-[#D4A24A]/60">{p.n}</span>
                <h3 className="font-playfair text-2xl mt-6 mb-3">{p.title}</h3>
                <p className="text-white/60 font-light leading-relaxed">{p.body}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-12">
            <Link to="/register" className="inline-flex items-center gap-2 text-[#D4A24A] text-sm font-semibold uppercase tracking-[0.2em] hover:gap-3 transition-[gap]" data-testid="pillars-cta">
              Explore founder access <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== Pricing ===== */}
      <section id="membership" className="py-24 md:py-32 bg-[#141416]/40">
        <div id="pricing" className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="text-center mb-16">
            <Eyebrow>Choose your path</Eyebrow>
            <h2 className="font-playfair text-3xl md:text-5xl font-medium tracking-tight mb-4">
              Membership designed for your next stage.
            </h2>
            <p className="text-white/60 text-lg font-light max-w-2xl mx-auto">
              Start with the tools you need now, then move into greater access, earning potential, and leadership.
            </p>
          </motion.div>

          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: '-80px' }} className="grid md:grid-cols-3 gap-8 items-start" data-testid="tiers-grid">
            {[
              { id: 'ascend', label: 'Ascend', cta: 'Get started', popular: false },
              { id: 'growth', label: 'Growth', cta: 'Join growth', popular: true },
              { id: 'founder', label: 'Founder', cta: 'Claim founder seat', popular: false },
            ].map((tCfg) => {
              const t = tiers?.[tCfg.id];
              if (!t) return null;
              return (
                <motion.div
                  key={tCfg.id}
                  variants={fadeUp}
                  className={`relative bg-[#141416] rounded-lg p-8 transition-[border-color,transform] hover:-translate-y-0.5 ${
                    tCfg.popular
                      ? 'border-2 border-[#D4A24A]/60 md:scale-[1.03]'
                      : 'border border-white/[0.08] hover:border-[#D4A24A]/30'
                  }`}
                  style={tCfg.popular ? { boxShadow: '0 0 40px rgba(212,162,74,0.15)' } : undefined}
                  data-testid={`tier-card-${tCfg.id}`}
                >
                  {tCfg.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4A24A] text-black text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1 rounded-sm">
                      Most popular
                    </div>
                  )}
                  <p className="text-xs uppercase tracking-[0.2em] text-white/50 font-semibold mb-3">{tCfg.label}</p>
                  <p className="font-playfair text-4xl mb-1">
                    ${t.price?.toFixed(2)}<span className="text-base text-white/40 font-sans">/mo</span>
                  </p>
                  <p className="text-sm text-white/40 mb-8">
                    {t.initiation_fee > 0 ? `+ $${t.initiation_fee.toFixed(0)} initiation fee` : 'No initiation fee'}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {(t.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3 text-white/70 text-sm font-light">
                        <Check className="w-4 h-4 text-[#D4A24A] flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className={`block text-center w-full py-3.5 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors ${
                      tCfg.popular
                        ? 'bg-[#D4A24A] text-black hover:bg-[#F59E0B]'
                        : 'border border-white/20 text-white/90 hover:border-[#D4A24A] hover:text-[#D4A24A]'
                    }`}
                    data-testid={`tier-cta-${tCfg.id}`}
                  >
                    {tCfg.cta}
                  </Link>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== Final CTA ===== */}
      <section className="py-24 md:py-32 bg-[#1C1C1F]">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Eyebrow>Founder seats are limited</Eyebrow>
          <h2 className="font-playfair text-3xl md:text-5xl font-medium tracking-tight mb-6">
            Build your empire with a system behind you.
          </h2>
          <p className="text-white/60 text-lg font-light mb-10">
            Join a community of serious entrepreneurs building real wealth through measurable
            performance, practical tools, and focused action.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4A24A] text-black font-semibold rounded-sm hover:bg-[#F59E0B] transition-colors text-sm uppercase tracking-wider"
            style={{ boxShadow: '0 0 25px rgba(212,162,74,0.25)' }}
            data-testid="final-cta"
          >
            Request founder access <ArrowRight size={16} />
          </Link>
        </motion.div>
      </section>

      <LegalFooter />
    </div>
  );
}
